import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@clerk/clerk-react';
import {
  fetchUserPermissions,
  clearPermissions,
  selectIsInitialized,
  selectIsLoadingPermissions,
} from '../features/rbac/rbacSlice';

/**
 * PermissionInitializer - Component that handles ONE-TIME permission fetching
 * 
 * This component should be placed at the app root level (inside SignedIn)
 * to ensure permissions are fetched exactly ONCE when user authenticates.
 * 
 * It uses a ref to track if fetch has been triggered to prevent race conditions.
 */
export function PermissionInitializer({ children }) {
  const dispatch = useDispatch();
  const { isSignedIn, isLoaded } = useUser();
  
  const isInitialized = useSelector(selectIsInitialized);
  const isLoading = useSelector(selectIsLoadingPermissions);
  
  // Use ref to prevent multiple fetches due to race conditions
  const hasFetched = useRef(false);

  // Fetch permissions ONCE when user signs in
  useEffect(() => {
    if (isLoaded && isSignedIn && !isInitialized && !isLoading && !hasFetched.current) {
      hasFetched.current = true;
      dispatch(fetchUserPermissions());
    }
  }, [isLoaded, isSignedIn, isInitialized, isLoading, dispatch]);

  // Reset the fetch flag and clear permissions when user signs out
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      hasFetched.current = false;
      if (isInitialized) {
        dispatch(clearPermissions());
      }
    }
  }, [isLoaded, isSignedIn, isInitialized, dispatch]);

  return children;
}

export default PermissionInitializer;

